/**
 * <QuizReviewQuestion> — une question du quiz de prérequis, relue (molecule).
 *
 * La ligne de l'ancien `PrerequisiteQuizDisplay`, côté élève : repliée, le numéro de la question
 * (à la couleur de ce qu'elle a donné), son énoncé, ce que l'élève a répondu (« Pas de réponse »
 * s'il n'a rien répondu), juste ou faux, et le chevron ; dépliée, toutes ses réponses, comme le
 * « Défi éclair » les marque une fois répondu (`<QuizChoiceRow>`) : la bonne en vert, celle que
 * l'élève a choisie à tort en rouge, les autres éteintes.
 *
 * Ce qui change : la bonne réponse que l'élève n'a pas choisie est verte, comme dans le défi (elle
 * était marine) ; « → 3 » devient « Ta réponse : 3 » ; un énoncé à formule (`textFormat="rich"`) se
 * rend comme celui d'une activité (`<RichText>`, Wajih, 18/10), les autres restent du texte. La
 * réponse donnée aussi (`answerFormat="rich"`), à la taille et à l'encre de sa ligne.
 *
 * Ouverte ou fermée est un état d'interface : il vit ici (`<Accordion>`). `defaultExpanded` l'ouvre
 * d'emblée.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Accordion } from "../Accordion"
import { QuizChoiceRow, type QuizChoiceState } from "../QuizChoiceRow"
import { quizChoiceLetter } from "../QuizChoiceRow/QuizChoiceRow"
import { RichText } from "../RichText"
import {
    type UIQuizReviewOptionMark,
    type UIQuizReviewQuestion,
} from "../../../ports/primary/ui/types/studentQuizReview"

export type QuizReviewQuestionProps = {
    /** Sa place dans le quiz, à partir de 0 (« Q1 » pour la première). */
    index: number
    question: UIQuizReviewQuestion
    /** Dépliée d'emblée. Défaut `false`, comme avant. */
    defaultExpanded?: boolean
    /**
     * À qui appartient la réponse : `student` (« Ta réponse », l'élève relit son quiz, défaut) ou
     * `tutor` (« Sa réponse », le masteur relit celui d'un élève en live).
     */
    reader?: "student" | "tutor"
    testID?: string
}

const TEXTS = {
    number: (index: number) => `Q${index + 1}`,
    answer: (answer: string) => `Ta réponse : ${answer}`,
    theirAnswer: (answer: string) => `Sa réponse : ${answer}`,
    unanswered: "Pas de réponse",
}

/** La marque d'une réponse, en état de la ligne du défi. */
const CHOICE_STATE: Record<UIQuizReviewOptionMark, QuizChoiceState> = {
    right: "right",
    wrong: "wrong",
    none: "inert",
}

export function QuizReviewQuestion(props: QuizReviewQuestionProps) {
    const { index, question, defaultExpanded = false, reader = "student", testID } = props
    const answerLabel = reader === "tutor" ? TEXTS.theirAnswer : TEXTS.answer
    const { theme } = useUnistyles()
    const right = question.result === "right"

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            testID={testID}
            header={(expanded) => (
                <View style={styles.header}>
                    <Badge text={TEXTS.number(index)} variant={right ? "success" : "danger"} tinted />
                    <Stack gap="1" style={styles.text}>
                        {question.textFormat === "rich" ? (
                            // La WebView de la formule ne prend pas le toucher : il replie la question. L'en-tête est
                            // un bouton, qui centre son texte sur le web : la question reste au début de la ligne.
                            <View pointerEvents="none">
                                <RichText value={question.text} align="start" compact />
                            </View>
                        ) : (
                            <Text size="text-base" weight="semibold" color="text-strong">
                                {question.text}
                            </Text>
                        )}
                        {question.answer != null && question.answerFormat === "rich" ? (
                            <View pointerEvents="none">
                                <RichText
                                    value={answerLabel(question.answer)}
                                    reading={{ adapted: false, font: "standard", size: theme.fontSizes["text-sm"] }}
                                    color="text-muted"
                                    align="start"
                                    compact
                                />
                            </View>
                        ) : (
                            <Text size="text-sm" color="text-muted">
                                {question.answer != null ? answerLabel(question.answer) : TEXTS.unanswered}
                            </Text>
                        )}
                    </Stack>
                    <Row gap="2">
                        <Icon
                            name={right ? "check" : "times"}
                            size={18}
                            color={right ? "success-strong" : "danger-strong"}
                        />
                        <Icon name={expanded ? "chevron-up" : "chevron-down"} size={16} color="text-muted" />
                    </Row>
                </View>
            )}
        >
            <Stack gap="2" style={styles.choices}>
                {question.options.map((option, i) => (
                    <QuizChoiceRow
                        key={`${i}-${option.label}`}
                        letter={quizChoiceLetter(i)}
                        label={option.label}
                        labelFormat={option.labelFormat}
                        state={CHOICE_STATE[option.mark]}
                    />
                ))}
            </Stack>
        </Accordion>
    )
}

const styles = StyleSheet.create((theme) => ({
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
        paddingVertical: theme.spacing["3"],
    },
    text: { flex: 1, flexShrink: 1 },
    choices: { paddingBottom: theme.spacing["3"] },
}))
